import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorResult, SketchPicker } from 'react-color';
import { combineClassNames } from '@common/utils/combineClassNames';
import { Icon } from '@common/components/icon';
import { Button } from '../../../button';

// Reuses the same palette as the shortcut color selector, plus white, black and grey.
export const STATUS_COLORS = [
  '#3C9EEF', '#33B29C', '#42B35D', '#F6AE35', '#F78C36',
  '#F76E63', '#F77995', '#F57ECB', '#9E74FA',
  '#FFFFFF', '#000000', '#F3F4F6',
];

// Human readable names for the swatches that aren't obvious from their hex value.
const COLOR_NAMES: Record<string, string> = {
  '#FFFFFF': 'White',
  '#000000': 'Black',
  '#F3F4F6': 'Grey',
};

// Returns true for colors light enough that a dark check reads better than a white one.
const isLightColor = (hex: string) => {
  const value = hex.replace('#', '');
  if (value.length !== 6) return false;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 186;
};

// Activates a handler on Enter/Space so the element behaves like a native button.
const onActivateKeyDown = (activate: () => void) => (event: React.KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    activate();
  }
};

interface Props {
  input: {
    value: string;
    onChange: (newColor: string) => void;
  };
  colors?: string[];
  className?: string;
}

export const SwatchColorInput = ({ input, colors = STATUS_COLORS, className }: Props) => {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  const close = () => {
    setShowPicker(false);
    setShowCustom(false);
  };

  return (
    <div className="ColorInput">
      <div
        role="button"
        tabIndex={0}
        aria-label={t('forms:form_form_color')}
        className={combineClassNames('ColorInput__Input', className)}
        onClick={() => setShowPicker(true)}
        onKeyDown={onActivateKeyDown(() => setShowPicker(true))}
      >
        <div
          className="ColorInput__Input__Preview"
          style={{ backgroundColor: input.value }}
        />
      </div>
      {showPicker && (
        <>
          <div
            className="ColorInput__Backdrop"
            onClick={close}
          />
          <div className="ColorInput__Picker">
            <div className="ColorInput__Picker__Inner">
              {showCustom ? (
                <>
                  <Button
                    icon="arrow_back"
                    onClick={() => setShowCustom(false)}
                    className="tw-mb-2"
                  >
                    {t('common:back')}
                  </Button>
                  <SketchPicker
                    disableAlpha
                    color={input.value}
                    presetColors={[]}
                    onChange={(color: ColorResult) => input.onChange(color.hex)}
                  />
                </>
              ) : (
                <>
                  <div className="tw-grid tw-grid-cols-5 tw-gap-2 tw-w-40">
                    {colors.map((hex) => (
                      <div
                        key={hex}
                        role="button"
                        tabIndex={0}
                        aria-label={COLOR_NAMES[hex.toUpperCase()] || hex}
                        onClick={() => {
                          input.onChange(hex);
                          close();
                        }}
                        onKeyDown={onActivateKeyDown(() => {
                          input.onChange(hex);
                          close();
                        })}
                        className="tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded
                          tw-cursor-pointer tw-border tw-border-solid tw-border-gray-custom-100"
                        style={{ backgroundColor: hex }}
                      >
                        {input.value?.toUpperCase() === hex.toUpperCase() && (
                          <Icon
                            type="check"
                            className={combineClassNames(
                              'tw-w-3 tw-h-3 tw-p-0.5 tw-rounded-full',
                              isLightColor(hex) ? 'tw-bg-gray-custom-100' : 'tw-bg-white',
                            )}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => setShowCustom(true)}
                    className="tw-mt-2 tw-w-full"
                    type="primary"
                  >
                    {t('forms:form_status_color_custom')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
